import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Heart, Target, MapPin, Users, TrendingUp, Check, Edit3, Zap, Send, RefreshCw, X } from 'lucide-react';
import { AIProfile } from '../types';

interface ProfileRevealScreenProps {
  profile: AIProfile;
  language: string;
  onEdit: () => void;
  onConfirm: () => void;
  onProfileUpdate?: (updatedProfile: AIProfile) => void;
}

const STRINGS = {
  Italian: {
    title: 'Ecco come ti vedo',
    subtitle: 'Ho analizzato le tue risposte e creato il tuo profilo iniziale',
    summary: 'Chi sei',
    coreValues: 'I tuoi valori',
    lifeGoals: 'I tuoi obiettivi',
    keyPeople: 'Persone importanti',
    keyPlaces: 'Luoghi significativi',
    traits: 'I tuoi tratti',
    question: 'Ti riconosci?',
    editButton: 'Modifica',
    confirmButton: 'Sì, sono io!',
    completeness: 'Completezza',
    building: 'Costruisco il tuo profilo...',
    analyzing: 'Analizzo le tue risposte...',
    extracting: 'Estraggo i tuoi valori...',
    connecting: 'Collego i punti...',
    ready: 'Pronto!',
    why: 'Perché',
    canModifyLater: 'Puoi sempre modificarlo dopo',
    editPlaceholder: 'Cosa vuoi cambiare? Es: "Non sono così creativo, sono più analitico"',
    profileUpdated: 'Profilo aggiornato',
    updating: 'Aggiorno...',
  },
  English: {
    title: 'Here\'s how I see you',
    subtitle: 'I analyzed your answers and created your initial profile',
    summary: 'Who you are',
    coreValues: 'Your values',
    lifeGoals: 'Your goals',
    keyPeople: 'Important people',
    keyPlaces: 'Significant places',
    traits: 'Your traits',
    question: 'Is this you?',
    editButton: 'Edit',
    confirmButton: 'Yes, that\'s me!',
    completeness: 'Completeness',
    building: 'Building your profile...',
    analyzing: 'Analyzing your answers...',
    extracting: 'Extracting your values...',
    connecting: 'Connecting the dots...',
    ready: 'Ready!',
    why: 'Why',
    canModifyLater: 'You can always modify it later',
    editPlaceholder: 'What to change? E.g., "I\'m not that creative, I\'m more analytical"',
    profileUpdated: 'Profile updated',
    updating: 'Updating...',
  },
  Spanish: {
    title: 'Así es como te veo',
    subtitle: 'Analicé tus respuestas y creé tu perfil inicial',
    summary: 'Quién eres',
    coreValues: 'Tus valores',
    lifeGoals: 'Tus objetivos',
    keyPeople: 'Personas importantes',
    keyPlaces: 'Lugares significativos',
    traits: 'Tus rasgos',
    question: '¿Eres tú?',
    editButton: 'Editar',
    confirmButton: '¡Sí, soy yo!',
    completeness: 'Completitud',
    building: 'Construyendo tu perfil...',
    analyzing: 'Analizando tus respuestas...',
    extracting: 'Extrayendo tus valores...',
    connecting: 'Conectando los puntos...',
    ready: '¡Listo!',
    why: 'Por qué',
    canModifyLater: 'Siempre puedes modificarlo después',
    editPlaceholder: '¿Qué cambiar? Ej: "No soy tan creativo, soy más analítico"',
    profileUpdated: 'Perfil actualizado',
    updating: 'Actualizando...',
  },
  German: {
    title: 'So sehe ich dich',
    subtitle: 'Ich habe deine Antworten analysiert und dein Anfangsprofil erstellt',
    summary: 'Wer du bist',
    coreValues: 'Deine Werte',
    lifeGoals: 'Deine Ziele',
    keyPeople: 'Wichtige Menschen',
    keyPlaces: 'Bedeutende Orte',
    traits: 'Deine Merkmale',
    question: 'Bist du das?',
    editButton: 'Bearbeiten',
    confirmButton: 'Ja, das bin ich!',
    completeness: 'Vollständigkeit',
    building: 'Erstelle dein Profil...',
    analyzing: 'Analysiere deine Antworten...',
    extracting: 'Extrahiere deine Werte...',
    connecting: 'Verbinde die Punkte...',
    ready: 'Fertig!',
    why: 'Warum',
    canModifyLater: 'Du kannst es später immer ändern',
    editPlaceholder: 'Was ändern? Z.B. "Ich bin nicht so kreativ, eher analytisch"',
    profileUpdated: 'Profil aktualisiert',
    updating: 'Aktualisiere...',
  },
  French: {
    title: 'Voici comment je te vois',
    subtitle: 'J\'ai analysé tes réponses et créé ton profil initial',
    summary: 'Qui tu es',
    coreValues: 'Tes valeurs',
    lifeGoals: 'Tes objectifs',
    keyPeople: 'Personnes importantes',
    keyPlaces: 'Lieux significatifs',
    traits: 'Tes traits',
    question: 'C\'est toi?',
    editButton: 'Modifier',
    confirmButton: 'Oui, c\'est moi!',
    completeness: 'Complétude',
    building: 'Construction de ton profil...',
    analyzing: 'Analyse de tes réponses...',
    extracting: 'Extraction de tes valeurs...',
    connecting: 'Connexion des points...',
    ready: 'Prêt!',
    why: 'Pourquoi',
    canModifyLater: 'Tu peux toujours le modifier plus tard',
    editPlaceholder: 'Quoi changer? Ex: "Je ne suis pas si créatif, je suis plus analytique"',
    profileUpdated: 'Profil mis à jour',
    updating: 'Mise à jour...',
  }
};

const ProfileRevealScreen: React.FC<ProfileRevealScreenProps> = ({ profile, language, onEdit, onConfirm, onProfileUpdate }) => {
  const [buildingStep, setBuildingStep] = useState(0);
  const [isBuilding, setIsBuilding] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profile);
  const [editInput, setEditInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const lang = language === 'Italian' ? 'Italian' : language === 'Spanish' ? 'Spanish' : language === 'German' ? 'German' : language === 'French' ? 'French' : 'English';
  const t = STRINGS[lang];

  const handleEditClick = () => {
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditInput('');
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editInput.trim() || isUpdating) return;

    setIsUpdating(true);

    try {
      const response = await fetch('/api/profile/chat-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentProfile: currentProfile,
          userRequest: editInput.trim(),
          language,
        }),
      });

      if (!response.ok) throw new Error('Failed to process edit');

      const data = await response.json();

      if (data.updatedProfile) {
        setCurrentProfile(data.updatedProfile);
        if (onProfileUpdate) {
          onProfileUpdate(data.updatedProfile);
        }
        setLastUpdate(data.response || editInput.trim());
        setShowUpdateBanner(true);
        setEditInput('');
        
        // Scroll to top to see the update banner
        contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Hide banner after 5 seconds
        setTimeout(() => setShowUpdateBanner(false), 5000);
      }
    } catch (error) {
      console.error('Error processing edit:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const buildingSteps = [t.analyzing, t.extracting, t.connecting, t.ready];

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setBuildingStep(prev => {
        if (prev >= buildingSteps.length - 1) {
          clearInterval(stepInterval);
          setTimeout(() => {
            setIsBuilding(false);
            setTimeout(() => setShowContent(true), 300);
          }, 800);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(stepInterval);
  }, []);

  if (isBuilding) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 animate-in fade-in duration-700">
        <div className="relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 border-4 border-indigo-200 rounded-full animate-ping opacity-20" />
            <div className="absolute w-24 h-24 border-4 border-purple-200 rounded-full animate-pulse" />
          </div>
          <div className="relative w-20 h-20 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl animate-bounce">
            <Sparkles size={32} className="text-white" />
          </div>
        </div>
        <div className="mt-12 text-center">
          <h2 className="text-2xl font-black text-black mb-2 animate-in slide-in-from-bottom-4 duration-500">
            {t.building}
          </h2>
          <p className="text-base font-bold text-indigo-600 animate-in slide-in-from-bottom-4 duration-500 delay-150">
            {buildingSteps[buildingStep]}
          </p>
        </div>
        <div className="flex gap-2 mt-8">
          {buildingSteps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                i <= buildingStep ? 'bg-indigo-600 scale-125' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col h-full ${showContent ? 'animate-in fade-in slide-in-from-bottom-8 duration-700' : 'opacity-0'}`}>
      {/* Scrollable content area */}
      <div ref={contentRef} className="flex-1 overflow-y-auto p-4 lg:p-6 pb-48">
        {/* Update Banner - shows when profile is modified */}
        {showUpdateBanner && lastUpdate && (
          <div className="mb-4 p-4 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl shadow-lg animate-in slide-in-from-top-4 duration-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                <Check size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-black text-white mb-0.5">{t.profileUpdated}</p>
                <p className="text-xs text-white/80 truncate">{lastUpdate}</p>
              </div>
              <button 
                onClick={() => setShowUpdateBanner(false)}
                className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center lg:text-left mb-6">
          <h1 className="text-2xl lg:text-3xl font-black text-black mb-2 leading-tight">
            {t.title}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            {t.subtitle}
          </p>
        </div>

        {/* Summary */}
        <div className="mb-5 p-5 bg-gray-50 rounded-2xl border border-gray-100">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 bg-black rounded-lg flex items-center justify-center">
              <Heart size={14} className="text-white" />
            </div>
            <h2 className="text-xs font-black text-black uppercase tracking-wider">{t.summary}</h2>
          </div>
          <p className="text-base font-medium text-gray-800 leading-relaxed">
            {currentProfile.summary}
          </p>
        </div>

        {/* Core Values */}
        {currentProfile.coreValues && currentProfile.coreValues.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Target size={16} className="text-indigo-600" />
              <h3 className="text-xs font-black text-black uppercase tracking-wider">{t.coreValues}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentProfile.coreValues.slice(0, 4).map((value, i) => (
                <span key={i} className="px-3 py-2 bg-indigo-50 text-indigo-800 rounded-xl text-sm font-bold border border-indigo-100">
                  {value.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Life Goals */}
        {currentProfile.lifeGoals && currentProfile.lifeGoals.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-emerald-600" />
              <h3 className="text-xs font-black text-black uppercase tracking-wider">{t.lifeGoals}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentProfile.lifeGoals.slice(0, 3).map((goal, i) => (
                <span key={i} className="px-3 py-2 bg-emerald-50 text-emerald-800 rounded-xl text-sm font-bold border border-emerald-100">
                  {goal.goal}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key People */}
        {currentProfile.keyPeople && currentProfile.keyPeople.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} className="text-rose-600" />
              <h3 className="text-xs font-black text-black uppercase tracking-wider">{t.keyPeople}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentProfile.keyPeople.slice(0, 4).map((person, i) => (
                <span key={i} className="px-3 py-2 bg-rose-50 text-rose-800 rounded-xl text-sm font-bold border border-rose-100">
                  {person.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Key Places */}
        {currentProfile.keyPlaces && currentProfile.keyPlaces.length > 0 && (
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-blue-600" />
              <h3 className="text-xs font-black text-black uppercase tracking-wider">{t.keyPlaces}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentProfile.keyPlaces.slice(0, 4).map((place, i) => (
                <span key={i} className="px-3 py-2 bg-blue-50 text-blue-800 rounded-xl text-sm font-bold border border-blue-100">
                  {place.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Personality Traits */}
        {currentProfile.personalityTraits && currentProfile.personalityTraits.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-purple-600" />
              <h3 className="text-xs font-black text-black uppercase tracking-wider">{t.traits}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentProfile.personalityTraits.slice(0, 5).map((trait, i) => (
                <span key={i} className="px-3 py-2 bg-purple-50 text-purple-800 rounded-xl text-sm font-bold border border-purple-100">
                  {trait.trait}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Bottom Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-50 safe-area-bottom">
        {/* Edit Input (shows when editing) */}
        {isEditing && (
          <form onSubmit={handleSubmitEdit} className="p-4 pb-2 border-b border-gray-100 bg-gray-50">
            <div className="flex gap-2 items-center">
              <input
                ref={inputRef}
                type="text"
                value={editInput}
                onChange={(e) => setEditInput(e.target.value)}
                placeholder={t.editPlaceholder}
                disabled={isUpdating}
                className="flex-1 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-0 outline-none text-sm disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={!editInput.trim() || isUpdating}
                className="w-12 h-12 bg-black text-white rounded-xl flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 transition-all shrink-0"
              >
                {isUpdating ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </button>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="w-12 h-12 bg-gray-200 text-gray-600 rounded-xl flex items-center justify-center active:scale-95 transition-all shrink-0"
              >
                <X size={18} />
              </button>
            </div>
          </form>
        )}

        {/* Action Buttons */}
        <div className="p-4 pt-3">
          <p className="text-center text-xs text-gray-400 mb-3 font-medium">
            {t.question} · {t.canModifyLater}
          </p>
          <div className="flex gap-3">
            <button
              onClick={isEditing ? handleCancelEdit : handleEditClick}
              className="flex-1 py-4 bg-gray-100 text-black font-bold rounded-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-sm"
            >
              <Edit3 size={16} />
              {t.editButton}
            </button>
            <button
              onClick={onConfirm}
              className="flex-[2] py-4 bg-black text-white font-bold rounded-2xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-sm shadow-lg"
            >
              <Check size={16} />
              {t.confirmButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileRevealScreen;
